/**
 * Unhinged Mugs — Collection Page Interactions
 *
 * Handles:
 *  - Swatch hover/tap → product image swap (desktop only; mobile is view-only)
 *  - Load-more button → AJAX append next page
 *  - Sort dropdown → reloads collection with sort_by param
 *  - Filter form → applies filters (native Shopify filter URLs)
 *
 * No dependencies. Plain fetch + DOM.
 */

(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 750;
  const isMobile = () =>
    window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;

  /* ============================================================
     SWATCH IMAGE SWAP
     ============================================================ */
  function handleSwatchInteraction(e) {
    const swatch = e.target.closest('[data-um-swatch]');
    if (!swatch) return;

    // Mobile swatches are pointer-events: none in CSS, but belt and suspenders
    if (isMobile()) return;

    // Prevent link navigation when clicking the swatch
    if (e.type === 'click') {
      e.preventDefault();
      e.stopPropagation();
    }

    const card = swatch.closest('[data-um-card]');
    if (!card) return;
    const imgWrap = card.querySelector('[data-um-card-img-wrap]');
    const img = card.querySelector('[data-um-card-img]');
    const swatches = card.querySelectorAll('[data-um-swatch]');
    const newImageUrl = swatch.dataset.imageUrl;
    const variantId = swatch.dataset.variantId;

    // Update active state on swatches
    swatches.forEach((s) => s.classList.remove('is-active'));
    swatch.classList.add('is-active');

    // Update card href to include variant param so PDP opens with this color
    if (variantId && card.dataset.productUrl) {
      const url = new URL(card.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      card.setAttribute('href', url.pathname + url.search);
    }

    // Swap image
    if (img && newImageUrl && img.tagName === 'IMG') {
      if (img.src === newImageUrl) return;

      // Preload before swap to avoid flash
      const preload = new Image();
      preload.onload = () => {
        img.classList.add('um-card__img--loading');
        requestAnimationFrame(() => {
          img.src = newImageUrl;
          img.classList.remove('um-card__img--loading');
        });
      };
      preload.onerror = () => {
        img.classList.remove('um-card__img--loading');
      };
      preload.src = newImageUrl;
    }
  }

  // Attach mouseenter directly — more reliable than mouseover delegation
  document.querySelectorAll('[data-um-swatch]').forEach(function(sw) {
    sw.addEventListener('mouseenter', function() {
      if (isMobile()) return;
      var card = sw.closest('[data-um-card]');
      if (!card) return;
      var img = card.querySelector('[data-um-card-img]');
      var newImageUrl = sw.dataset.imageUrl;
      var variantId = sw.dataset.variantId;
      card.querySelectorAll('[data-um-swatch]').forEach(function(s) { s.classList.remove('is-active'); });
      sw.classList.add('is-active');
      if (variantId && card.dataset.productUrl) {
        var url = new URL(card.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', variantId);
        card.setAttribute('href', url.pathname + url.search);
      }
      if (img && newImageUrl) {
        img.srcset = '';
        img.src = newImageUrl;
      }
    });
  });
  document.addEventListener('focusin', (e) => {
    if (e.target.closest('[data-um-swatch]')) handleSwatchInteraction(e);
  });
  document.addEventListener('click', handleSwatchInteraction);

  /* ============================================================
     LOAD MORE
     ============================================================ */
  async function handleLoadMore(btn) {
    const nextUrl = btn.dataset.nextUrl;
    if (!nextUrl) return;

    btn.setAttribute('aria-busy', 'true');
    const originalText = btn.textContent;
    btn.textContent = 'Loading...';

    try {
      const res = await fetch(nextUrl, {
        headers: { Accept: 'text/html' },
      });
      if (!res.ok) throw new Error('Load failed');
      const html = await res.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract new cards
      const newCards = doc.querySelectorAll('[data-um-grid] > [data-um-card]');
      const currentGrid = document.querySelector('[data-um-grid]');
      if (!currentGrid) throw new Error('Grid not found');

      newCards.forEach((card) => currentGrid.appendChild(card));

      // Update the load-more button with next page's URL
      const newLoadMore = doc.querySelector('[data-um-loadmore]');
      if (newLoadMore && newLoadMore.dataset.nextUrl) {
        btn.dataset.nextUrl = newLoadMore.dataset.nextUrl;
        btn.dataset.currentPage = newLoadMore.dataset.currentPage;
      } else {
        // No more pages
        btn.setAttribute('disabled', '');
        btn.dataset.nextUrl = '';
        btn.textContent = 'All loaded';
      }

      // Update counter
      const shownEl = document.querySelector('[data-um-shown]');
      if (shownEl) {
        shownEl.textContent = currentGrid.querySelectorAll('[data-um-card]').length;
      }

      // Update URL for back-button restoration (without navigating)
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, '', nextUrl);
      }

      if (!btn.hasAttribute('disabled')) {
        btn.textContent = originalText;
      }
    } catch (err) {
      console.error('[UM Collection] Load more failed:', err);
      btn.textContent = 'Try again';
    } finally {
      btn.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-um-loadmore]');
    if (btn && !btn.hasAttribute('disabled')) handleLoadMore(btn);
  });

  /* ============================================================
     SORT DROPDOWN
     ============================================================ */
  document.addEventListener('change', (e) => {
    const sort = e.target.closest('[data-um-sort]');
    if (!sort) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ============================================================
     FILTER FORM — submits immediately on checkbox change
     ============================================================ */
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-um-filter-form] input');
    if (!input) return;

    const form = input.closest('[data-um-filter-form]');
    const allInputs = document.querySelectorAll('[data-um-filter-form] input');
    const url = new URL(window.location.href);

    // Clear existing filter params
    [...url.searchParams.keys()]
      .filter((k) => k.startsWith('filter.'))
      .forEach((k) => url.searchParams.delete(k));

    // Apply all currently-checked filters (across all filter forms)
    allInputs.forEach((inp) => {
      if (inp.checked) {
        url.searchParams.append(inp.name, inp.value);
      }
    });

    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ============================================================
     Close filter dropdowns when clicking outside
     ============================================================ */
  document.addEventListener('click', (e) => {
    const allFilters = document.querySelectorAll('[data-um-filter][open]');
    allFilters.forEach((f) => {
      if (!f.contains(e.target)) f.removeAttribute('open');
    });
  });

  window.UMCollection = { handleLoadMore };
})();
